import { useEffect, useRef, useState } from "react";
import { MousePointerClick } from "lucide-react";

const DESIGN_W = 1280;
const CONTENT_H = 2600; // tall enough to capture most of a landing page

/**
 * Live embedded preview of an external site.
 * At rest it shows an elegant poster; on hover it reveals the REAL site in an
 * iframe (pointer-events disabled so the parent link handles clicks) that
 * slowly pans down to simulate scrolling through the page.
 */
export default function LivePreview({ url, poster, title }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.4);
  const [contH, setContH] = useState(520);
  const [loaded, setLoaded] = useState(false);
  const [hover, setHover] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      setScale(el.clientWidth / DESIGN_W);
      setContH(el.clientHeight);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scaledHeight = CONTENT_H * scale;
  const pan = Math.max(0, scaledHeight - contH);
  const liveVisible = hover && loaded;

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 overflow-hidden bg-[#0a0a09]"
      onMouseEnter={() => {
        setHover(true);
        setMounted(true);
      }}
      onMouseLeave={() => setHover(false)}
    >
      {/* Elegant poster — always the resting state */}
      <img
        src={poster}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Live site, mounted on first hover, revealed once loaded */}
      {mounted && (
        <div
          style={{
            transform: `translateY(${hover ? -pan : 0}px)`,
            transition: hover
              ? "transform 7s linear, opacity 0.6s ease"
              : "transform 1.2s cubic-bezier(0.16,1,0.3,1), opacity 0.6s ease",
            opacity: liveVisible ? 1 : 0,
          }}
          className="absolute inset-x-0 top-0"
        >
          <iframe
            src={url}
            title={title}
            loading="lazy"
            scrolling="no"
            tabIndex={-1}
            sandbox="allow-scripts allow-same-origin"
            onLoad={() => setLoaded(true)}
            style={{
              width: DESIGN_W,
              height: CONTENT_H,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              border: 0,
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {/* Hover hint — fades out once live view is showing */}
      <div
        style={{ opacity: liveVisible ? 0 : 1 }}
        className="absolute top-6 left-1/2 -translate-x-1/2 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15 text-[10px] uppercase tracking-[0.2em] text-white/70 transition-opacity duration-500 pointer-events-none"
      >
        <MousePointerClick size={12} /> Hover to preview
      </div>
    </div>
  );
}
